import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

/** Founder/admin-only. Bulk-export attendance as .xlsx, including a per-row
 * Late flag and each person's current-month late count — for payroll and
 * performance reviews. Styled 1:1 with LeavesExportDialog.jsx. */
export default function AttendanceExportDialog() {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [exporting, setExporting] = useState(false);

  const runExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/attendance/export", { params: { year }, responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-export-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      setOpen(false);
    } catch (e) {
      toast.error(`Export failed: ${e?.response?.status || e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" data-testid="attendance-export-open-btn">
          <Download size={12} className="mr-1.5" />
          Bulk export
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md" data-testid="attendance-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#F54800]" /> Export attendance history
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Downloads an .xlsx with a Late flag and each person&rsquo;s current-month late count on every row — ready for payroll or performance reviews.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="label-overline">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="attendance-export-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={runExport} disabled={exporting} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="attendance-export-run-btn">
            <Download size={13} className="mr-1.5" /> {exporting ? "Exporting…" : "Download .xlsx"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
